import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

const Dashboard = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [checking, setChecking] = useState(true);

  useEffect(() => {
    const checkUserRole = async () => {
      if (authLoading) return;

      if (!user) {
        navigate('/auth');
        return;
      }

      try {
        const { data: usuario, error: usuarioError } = await supabase
          .from('usuarios')
          .select('papel, empresa_id')
          .eq('id', user.id)
          .maybeSingle();

        if (usuarioError) {
          console.error('Error fetching usuario:', usuarioError);
          throw usuarioError;
        }

        // Admin sempre vai direto para o painel admin, sem precisar de empresa/briefing
        const metadataPapel = user.user_metadata?.papel;
        if (usuario?.papel === 'admin' || metadataPapel === 'admin') {
          navigate('/admin', { replace: true });
          return;
        }

        if (!usuario) {
          // Verifica se usuário foi convidado (tem empresa_id nos metadados)
          const metaEmpresaId = user.user_metadata?.empresa_id;
          const metaPapel = metadataPapel;
          // Recupera plano pendente dos metadados OU localStorage
          const pendingPlan = user.user_metadata?.pending_plan || localStorage.getItem('pending_plan');
          
          // Salva o plano pendente no localStorage para garantir que não se perca
          if (pendingPlan && pendingPlan !== 'start') {
            localStorage.setItem('pending_plan', pendingPlan);
          }
          
          // Create usuario record
          const { error: insertError } = await supabase.from('usuarios').insert({
            id: user.id,
            email: user.email,
            nome: user.user_metadata?.nome || user.email?.split('@')[0] || 'Usuário',
            papel: metaPapel || 'coordenador',
            empresa_id: metaEmpresaId || null,
          });
          
          if (insertError) {
            console.error('Error creating usuario:', insertError);
            if (insertError.code === '23505') {
              const { data: existingUsuario } = await supabase
                .from('usuarios')
                .select('papel, empresa_id')
                .eq('id', user.id)
                .maybeSingle();

              if (existingUsuario?.papel === 'admin') {
                navigate('/admin', { replace: true });
                return;
              }
            }
          }
          
          // Se foi convidado (tem empresa), vai direto para o dashboard apropriado
          if (metaEmpresaId) {
            if (metaPapel === 'coordenador') {
              navigate('/coordenador', { replace: true });
            } else {
              navigate('/vendedor', { replace: true });
            }
            return;
          }
          
          // Se se cadastrou sozinho, vai para briefing criar empresa
          // Manter o plano pendente para depois do briefing
          navigate('/briefing', { replace: true });
        } else if (!usuario.empresa_id) {
          // Se não tem empresa, vai para briefing
          navigate('/briefing', { replace: true });
        } else {
          // Verificar se empresa tem briefing preenchido
          const { data: empresa } = await supabase
            .from('empresas')
            .select('produto, publico, proposta_valor, plano')
            .eq('id', usuario.empresa_id)
            .single();

          if (!empresa?.produto || !empresa?.publico || !empresa?.proposta_valor) {
            navigate('/briefing', { replace: true });
            return;
          }
          
          // Verificar se tem plano pendente para upgrade
          const pendingPlan = localStorage.getItem('pending_plan');
          if (pendingPlan && pendingPlan !== 'start' && pendingPlan !== empresa.plano) {
            // Limpar o plano pendente e redirecionar para upgrade
            localStorage.removeItem('pending_plan');
            navigate('/upgrade?plan=' + pendingPlan, { replace: true });
            return;
          }
          
          // Limpar qualquer plano pendente
          localStorage.removeItem('pending_plan');
          
          if (usuario.papel === 'coordenador') {
            navigate('/coordenador', { replace: true });
          } else {
            navigate('/vendedor', { replace: true });
          }
        }
      } catch (error) {
        console.error('Error checking user role:', error);
        // Não redireciona para /auth em erro de rede transitório:
        // o usuário ainda está autenticado e isso causaria loop dashboard↔auth.
        // Apenas mantém a tela de carregamento; o próximo evento de auth dispara nova tentativa.
      } finally {
        setChecking(false);
      }
    };

    checkUserRole();
  }, [user, authLoading, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-accent/10 flex items-center justify-center">
      <div className="text-center space-y-4">
        <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto" />
        <p className="text-muted-foreground">Carregando...</p>
      </div>
    </div>
  );
};

export default Dashboard;